import * as React from 'react';
import { TierFeature, Settings } from '../../Model';
import { Toggle, TrustpilotSpinner, NotificationAlert, NotificationType } from '../../CommonUI';
import Button from '../../CommonUI/Control/Button';
import { consentIsAccepted } from '../../Api/businessConsentsClient';
import { Auth } from '@trustpilot/ecommscript-integrationappauth';
import { Flex } from '../../CommonUI/Layout';
import { getBusinessUnitTierFeatures } from '../../Api/tiersClient';
import { Text } from 'ilenia';
import { integrationConfig, getIntegrationConfig } from '../../Api/configapiClient';

const upgradeUrl = process.env.REACT_APP_UpgradeTierUrl;

interface ProductReviewsToggleProps {
    integrationKey: string;
    source: string;
    tierName: string;
    sku: string;
    productIdentificationOptions?: string[];
    appearance?: 'bold' | 'subtle';
    mt?: number;
    pl?: number;
    onProductReviewsSettingChange: (selector: Settings) => void;
    onLoggedInChange: (toggle: boolean) => void;
    onSettingsSave: () => void;
    toggleChanged?: (value: boolean) => void;
    onConsentChange: (toggle: boolean) => void;
    onToggleSettingsSave: (value: boolean) => void;
}

interface ProductReviewsToggleState {
    enableProductReviews?: boolean;
    allowProductReviews: boolean;
    businessUnitId: string;
    saveError?: string;
    notificationIsDismissed: boolean;
    skuScannerIsOpen: boolean;
}

export class ProductReviewsToggle extends React.Component<ProductReviewsToggleProps, ProductReviewsToggleState> {
    private mounted = true;

    constructor(props: ProductReviewsToggleProps) {
        super(props);
        this.state = {
            enableProductReviews: false,
            allowProductReviews: false,
            businessUnitId: '',
            notificationIsDismissed: false,
            skuScannerIsOpen: false,
        };
        this.onProductReviewsToggle = this.onProductReviewsToggle.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onNotificationClose = this.onNotificationClose.bind(this);
        this.onUpgradeHandler = this.onUpgradeHandler.bind(this);
        this.skuIsNone = this.skuIsNone.bind(this);
    }

    public onSave(enableProductReviews: boolean) {
        const { businessUnitId } = this.state;
        this.props.onSettingsSave();
        this.props.onToggleSettingsSave(enableProductReviews);
        integrationConfig(businessUnitId, { enableProductReviews })
            .then((result) => {
                if (this.mounted) {
                    if (result) {
                        this.setState({saveError: 'Notifications.saveMessage'});
                    } else {
                        this.setState({saveError: 'invitations.error'});
                    }
                }
            });
    }

    public checkAuthAndConsent(integrationKey: string) {
        return Promise.all([
            Auth.isLoggedIn(process.env.REACT_APP_AuthenticationApiUrl!, process.env.REACT_APP_CookiesDomain!),
            consentIsAccepted(integrationKey)
        ]);
    }

    public componentDidMount() {
        analytics.page();
        const { integrationKey } = this.props;
        if (integrationKey) {
            this.checkAuthAndConsent(integrationKey).then((response) => {
                const [isLoggedIn, consentResponse] = response;
                if (isLoggedIn) {
                    if (consentResponse && consentResponse.consentIsAccepted) {
                        this.getProductReviewTabSettings(consentResponse.businessUnitId).then((settings) => {
                            const [features, config] = settings;
                            if (features && features.length > 0 && config) {
                                if (this.mounted) {
                                    this.setState({
                                        enableProductReviews: config.enableProductReviews,
                                        businessUnitId: consentResponse.businessUnitId,
                                        allowProductReviews: (features.indexOf(TierFeature.Reviews_ProductReviews) > -1),
                                    });
                                }
                            } else {
                                NotificationAlert.alert(NotificationType.Error, 'Notifications.loadDataError');
                            }
                        });
                    } else {
                        this.props.onConsentChange(false);
                    }
                } else {
                    this.props.onLoggedInChange(false);
                }
            });
        }
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    public getProductReviewTabSettings(businessUnitId: string) {
        return Promise.all([
            getBusinessUnitTierFeatures(businessUnitId),
            getIntegrationConfig(businessUnitId),
        ]);
    }

    public onChange(next: (event: any) => void) {
        return (event: any) => {
            return next(event);
        };
    }

    public onProductReviewsToggle(value: boolean) {
        if (this.props.toggleChanged) {
            this.props.toggleChanged(value);
        }
        const enableProductReviews = value;
        if (enableProductReviews && this.skuIsNone()) {
            this.onSelectChange('skuSelector', this.props.productIdentificationOptions![1]);
        }
        this.setState({ enableProductReviews });
        this.onSave(enableProductReviews);
    }

    public onSelectChange(type: string, value: string) {
        const newSettings = {};
        newSettings[type] = value;
        if (value && value !== 'none') {
            this.setState({enableProductReviews: true});
        }
        this.props.onProductReviewsSettingChange(newSettings as Settings);
    }

    public skuIsNone() {
        const { sku } = this.props;
        return !sku || sku === 'none';
    }

    public onNotificationClose() {
        this.setState({ notificationIsDismissed: true });
    }

    public onUpgradeHandler() {
        const { tierName, source } = this.props;
        analytics.track('Integration Link Clicked', {
            context: 'Ecommscript integration app',
            location: window.location.pathname,
            integration: source,
            placement: 'General tab form',
            action: `Upgrade tier page in business app`,
            name: `upgrade-tier-link`,
            source: this.props.source,
            tierName,
        });
        window.open(upgradeUrl, '_blank');
    }

    public render() {
        const { tierName, appearance = 'bold', mt = 20, pl } = this.props;
        const { allowProductReviews, enableProductReviews, businessUnitId } = this.state;
        if (!businessUnitId) {
            return <TrustpilotSpinner active={true} height="20px"/>;
        }
        const upgradeButton = (
            <Button
                onClick={this.onUpgradeHandler}
                width="102px"
                pl={0}
                pr={0}
                track={{
                    placement: 'General tab form',
                    action: `Upgrade tier page in business app`,
                    name: `upgrade-tier-link`,
                    source: this.props.source,
                    tierName,
                }}
            >
                <Text id={'general.productReview.upgrade'} />
            </Button>
        );

        const toggle = (
            <Toggle
                mt={mt}
                pl={pl}
                id="toggle"
                checked={allowProductReviews && enableProductReviews}
                onChange={this.onChange(this.onProductReviewsToggle)}
                disabled={!allowProductReviews}
                track={{
                    placement: 'General tab form',
                    action: `Toggle product reviews`,
                    name: `toggle-product-reviews`,
                    source: this.props.source,
                    tierName,
                }}
                appearance={appearance}
            />
        );
        return (
            <Flex>
                {allowProductReviews ? toggle : upgradeButton}
            </Flex>
        );
    }
}



// WEBPACK FOOTER //
// ./src/Components/Common/ProductReviewsToggle.tsx